<template>
  <div />
</template>

<script>
/**
 * Vue: Scroll Tracker (scroll-tracker)
 * -----------------------------------------------------------------------------
 * Renderless component that tracks scroll depth at 25% thresholds.
 * Tracks downward scroll only and fires each threshold once.
 *
 * @param {String} eventSource - EventBus event name to listen to,
 *   or 'window' for window scroll.
 * @param {Function} getScrollPosition - Returns current scroll
 *   position in px. Receives the EventBus payload as an argument
 *   when eventSource is not 'window'.
 * @param {Function} getScrollableHeight - Returns total scrollable
 *   height in px. Called on each scroll event.
 * @param {Function} buildEvent - Receives a percent (25/50/75/100)
 *   and returns the event object to pass to track().
 */

import { track } from '~/helpers/datalayer-actions'

const PERCENTS = [25, 50, 75, 100]

export default {
  name: 'ScrollTracker',

  props: {
    eventSource: {
      type: String,
      required: true,
    },
    getScrollPosition: {
      type: Function,
      required: true,
    },
    getScrollableHeight: {
      type: Function,
      required: true,
    },
    buildEvent: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      scrollY: 0,
      trackedPercents: {
        25: false,
        50: false,
        75: false,
        100: false,
      },
    }
  },

  mounted() {
    if (this.eventSource === 'window') {
      this.scrollY = window.scrollY
      this.initTrackedPercents(this.getScrollPosition())
      window.addEventListener('scroll', this.handleScroll, { passive: true })
    } else {
      cnvs.EventBus.listen(this.eventSource, this.handleEventBusScroll)
    }
  },

  beforeUnmount() {
    if (this.eventSource === 'window') {
      window.removeEventListener('scroll', this.handleScroll)
    } else {
      cnvs.EventBus.remove(this.eventSource)
    }
  },

  methods: {
    /**
     * Handle window scroll events.
     */
    handleScroll() {
      const scrollPosition = this.getScrollPosition()
      const scrollableHeight = this.getScrollableHeight()
      this.processScroll(scrollPosition, scrollableHeight)
    },

    /**
     * Handle EventBus scroll events.
     * @param {Object} payload - EventBus payload.
     */
    handleEventBusScroll(payload) {
      const scrollPosition = this.getScrollPosition(payload)
      const scrollableHeight = this.getScrollableHeight()
      this.processScroll(scrollPosition, scrollableHeight)
    },

    /**
     * Process a scroll position and fire events for crossed thresholds.
     * @param {number} scrollPosition - Current scroll position in px.
     * @param {number} scrollableHeight - Total scrollable height in px.
     */
    processScroll(scrollPosition, scrollableHeight) {
      const direction = scrollPosition > this.scrollY ? 'down' : 'up'
      this.scrollY = scrollPosition

      if (scrollableHeight <= 0) {
        return
      }

      const currentPercent = (scrollPosition / scrollableHeight) * 100
      const percentsToTrack = []

      for (let index = 0; index < PERCENTS.length; index++) {
        const percent = PERCENTS[index]
        if (currentPercent >= percent) {
          if (!this.trackedPercents[percent]) {
            percentsToTrack.push(percent)
            this.trackedPercents[percent] = true
          }
        } else {
          break
        }
      }

      if (direction === 'up' || percentsToTrack.length === 0) {
        return
      }

      percentsToTrack.forEach((percent) => {
        track(this.buildEvent(percent))
      })
    },

    /**
     * Pre-mark thresholds already passed on mount.
     * @param {number} currentPercent - Current scroll percentage.
     */
    initTrackedPercents(currentPercent) {
      if (currentPercent < 25) {
        return
      }
      PERCENTS.forEach((percent) => {
        if (percent <= currentPercent) {
          this.trackedPercents[percent] = true
        }
      })
    },
  },
}
</script>
